/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { compose } from 'recompose';

import config from 'js/app/config';
import connectToRouter from 'js/lib/connectToRouter';
import type { InjectedRouter } from 'js/lib/connectToRouter';

import { Button, Typography, useTheme } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import CourseraLogo from 'bundles/page/components/CourseraLogo';
import TrackedButton from 'bundles/page/components/TrackedButton';

import _t from 'i18n!nls/tutorials';

type Props = {
  router: InjectedRouter;
};

const YELLOW_LOGO_C = `${config.url.resource_assets}articles/masked-coursera-c.svg`;
const MOBILE_YELLOW_LOGO_C = `${config.url.resource_assets}articles/Cropped-top-and-bottom-C.svg`;

const styles = {
  consumerSignupCtaContainer: (theme: Theme) =>
    css({
      backgroundColor: 'var(--cds-color-yellow-25)',
      backgroundImage: `url(${YELLOW_LOGO_C})`,
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'auto 100%',
      display: 'flex',
      height: '330px',
      flexDirection: 'column-reverse',
      overflow: 'hidden',
      position: 'relative',
      '@media screen and (max-width: 480px)': {
        backgroundImage: `url(${MOBILE_YELLOW_LOGO_C})`,
      },
    }),
  consumerSignupCtaSection: css({
    overflow: 'hidden',
    padding: '50px 0px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    zIndex: 10,
    '@media screen and (max-width: 480px)': {
      margin: '0 auto',
      width: '50%',
    },
  }),
  ctaText: css({
    textAlign: 'center',
  }),
  image: css({
    width: 117,
    height: 16,
    marginBottom: 55,
    marginTop: 17,
    '@media screen and (max-width: 480px)': {
      marginBottom: 25,
    },
  }),
};

const handleConsumerCTAClick = (router: InjectedRouter) => {
  router.replace({
    pathname: router?.location?.pathname || '/',
    query: { ...(router?.location?.query || {}), authMode: 'signup' },
  });
};

export const ConsumerSignupCTA = ({ router }: Props) => {
  const theme = useTheme();
  const trackEvent = useTracker();
  const handleOnClick = () => {
    handleConsumerCTAClick(router);
    trackEvent('click_button', {
      button: {
        name: 'signup' as const,
      },
      pageSection: { sectionName: 'bottom_banner' },
    });
  };
  return (
    <section className="rc-ConsumerSignupCtaContainer" css={styles.consumerSignupCtaContainer(theme)}>
      <div css={styles.consumerSignupCtaSection}>
        <Typography variant="d2semibold" component="h2" css={styles.ctaText}>
          {_t('Learn without limits')}
        </Typography>
        <CourseraLogo css={styles.image} />
        <Button
          variant="primary"
          trackingName="secondary_consumer_join_for_free"
          withVisibilityTracking={false}
          requireFullyVisible={false}
          onClick={handleOnClick}
          component={TrackedButton}
          className="joinForFree"
        >
          {_t('Join for Free')}
        </Button>
      </div>
    </section>
  );
};

export default compose<Props, {}>(connectToRouter((router) => ({ router })))(ConsumerSignupCTA);
